import { CommonModule } from '@angular/common';
import { Component, inject } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { MatButton } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatDialog } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';

import { RouterLink } from '@angular/router';
import { SelectedDomainFactoryService } from '@galaxy/email-ui/domain-authorization/src/sender-info-card/sender-info-form/selected-domain-factory.service';
import { TranslateModule } from '@ngx-translate/core';
import { SenderType, SendersService } from '@vendasta/email';
import { GalaxyFormFieldModule } from '@vendasta/galaxy/form-field';
import { GalaxyPopoverModule } from '@vendasta/galaxy/popover';
import { GalaxyTooltipModule } from '@vendasta/galaxy/tooltip';
import { EMPTY, Observable, catchError, combineLatest, map, switchMap } from 'rxjs';
import { ACCOUNT_GROUP_ID_TOKEN, COMPANY_NAME_TOKEN, InboxService } from '../../../../core/src';

interface ForwardingEmailTitle {
  firstName: string;
  companyName: string;
  translationKey: string;
}

@Component({
  selector: 'inbox-email-settings-card',
  imports: [
    CommonModule,
    MatIconModule,
    MatCardModule,
    TranslateModule,
    GalaxyPopoverModule,
    MatButton,
    GalaxyFormFieldModule,
    GalaxyTooltipModule,
    FormsModule,
    RouterLink,
  ],
  templateUrl: './email-settings-card.component.html',
  styleUrl: './email-settings-card.component.scss',
})
export class EmailSettingsCardComponent {
  private readonly selectedDomainFactoryService = inject(SelectedDomainFactoryService);
  private readonly sendersService = inject(SendersService);
  private readonly accountGroupId = toSignal(inject(ACCOUNT_GROUP_ID_TOKEN));

  private readonly user$ = inject(InboxService).iamUser$;
  private readonly companyName$ = inject(COMPANY_NAME_TOKEN);
  readonly senderInfoEmailAddress = toSignal(this.getSenderInfoEmailAddress(), { initialValue: '' });

  constructor(private dialog: MatDialog) {}

  protected showPopover = false;
  forwardingEmailTitle$: Observable<ForwardingEmailTitle> = combineLatest([this.user$, this.companyName$]).pipe(
    map(([user, companyName]) => ({
      firstName: user.firstName,
      companyName: companyName,
      translationKey: user.firstName
        ? 'INBOX.SETTINGS.EMAIL_SETTINGS.USER_AT_COMPANY'
        : 'INBOX.SETTINGS.EMAIL_SETTINGS.COMPANY_ONLY',
    })),
  );

  getSenderInfoEmailAddress(): Observable<string> {
    return this.sendersService.getEmailSendingInfo(SenderType.SENDER_TYPE_BUSINESS, this.accountGroupId() || '').pipe(
      switchMap((senderInfo) =>
        this.selectedDomainFactoryService
          .fromSenderInfo(SenderType.SENDER_TYPE_BUSINESS, senderInfo, !senderInfo.isDefaultForSenderType)
          .pipe(map((selectedDomain) => `${senderInfo.preferredEmailUsername}@${selectedDomain.domain}`)),
      ),
      catchError(() => EMPTY),
    );
  }
}
